import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signupUser } from "../apis/auth";
import { toast } from "react-toastify";
import { UserPlus, Shield, Eye, EyeOff } from "lucide-react";
import Lottie from "lottie-react";
import animationData from "../animation/signup.json"; // your animated Lottie JSON
import logo from "../assets/i2.png"; // your logo file (SVG or PNG)

const Signup = () => {
  const [form, setForm] = useState({
    email: "",
    name: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Blutec PING Signup - Create Your Free WhatsApp Messaging Account";
    const description =
      "Sign up for Blutec PING and start free WhatsApp broadcasting, bulk messaging, and chatbot automation. Quick setup—get instant access to smart business tools.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      toast.error("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await signupUser({ ...form, confirmPassword: undefined });
      toast.success("Signup successful! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const message = err.response?.data?.message || "Signup failed.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className=" flex bg-white text-gray-900">
      {/* Left: Logo + Lottie + Text */}
      <div
        className="flex-1 hidden md:flex flex-col justify-center items-center bg-gray-50"
      >
        {/* Logo */}
        <img src={logo} alt="Blutec PING" className="w-36 mb-4 drop-shadow-lg" />
        {/* Welcome Text */}
        <div className="mb-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900">Welcome to Blutec PING!</h2>
          <p className="text-gray-600 text-base mt-1 font-medium">
            Fast, secure & reliable messaging.
            <br />
            Sign up to get started.
          </p>
        </div>
        {/* Lottie Animation */}
        <div className="w-[60%] max-w-md">
          <Lottie
            animationData={animationData}
            loop
            autoplay
            style={{ width: "100%", height: "auto" }}
          />
        </div>
      </div>
      {/* Right: Signup Form */}
      <div className="flex-1 flex items-center justify-center bg-white">
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-5 px-7 py-10">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-1">Create Account</h1>
          <p className="text-gray-600 mb-2">Join us today and get access to amazing features</p>
          <div className="flex items-center mb-4">
            <span className="flex items-center gap-2 bg-[#01CC54]/10 text-[#01CC54] text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm border border-[#01CC54]/20">
              <Shield size={16} className="text-[#01CC54]" />
              <span className="text-gray-900">Secure Account Registration</span>
            </span>
          </div>
          {error && (
            <div className="mb-2 flex items-center gap-2 bg-rose-50 border-l-4 border-rose-500 rounded-md p-3 text-rose-700 text-sm font-medium">
              {error}
            </div>
          )}
          <input
            name="name"
            type="text"
            placeholder="Enter your full name"
            autoComplete="name"
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
          />
          <input
            name="phone"
            type="text"
            placeholder="Enter your phone number"
            autoComplete="tel"
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
          />
          <input
            name="email"
            type="email"
            placeholder="Enter your email"
            autoComplete="email"
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
          />
          {/* Password Field with show/hide */}
          <div className="relative w-full">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Create password"
              onChange={handleChange}
              required
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54]"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>
          {/* Confirm Password Field with show/hide */}
          <div className="relative w-full">
            <input
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm password"
              onChange={handleChange}
              required
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54]"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className={`
              w-full flex items-center justify-center gap-2
              rounded-xl py-3 text-lg font-bold text-white
              bg-[#048AAD]
              shadow-lg transition
              hover:scale-105 hover:shadow-xl hover:bg-[#048AAD]/90
              focus:outline-none focus:ring-2 focus:ring-[#048AAD]/40
              disabled:opacity-60 disabled:cursor-not-allowed
            `}
            aria-busy={isLoading}
          >
            <UserPlus size={18} className="mr-2" />
            {isLoading ? "Creating Account..." : "Create Account"}
          </button>

          <div className="mt-2 text-gray-500 text-xs text-center">
            <span className="mr-1">🔒</span>Your data is secure and protected
          </div>
          <p className="text-sm text-center mt-1 text-gray-600">
            Already have an account?{" "}
            <a href="/login" className="text-[#01CC54] font-semibold hover:underline">
              Sign In
            </a>
          </p>
          <p className="text-xs text-gray-500 text-center mt-2">
            By creating an account, you agree to our terms and conditions
          </p>
        </form>
      </div>
    </div>
  );
};

export default Signup;
